import React, { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { useFeatureToggle } from '@flopflip/react-redux';
import { Box, Typography } from '@mui/material';
import { onAutocompleteChangeInForm } from 'app/shared/components/form/Autocomplete';
import RoundedBox from 'app/shared/componentsRestyled/common/RoundedBox';
import { AutocompleteCDS } from 'app/shared/componentsRestyled/coreDesignSystem/form';
import FormGeneratorSwitchCDS from 'app/shared/componentsRestyled/coreDesignSystem/formGenerator/FormGeneratorSwitchCDS';
import PopoverCDS from 'app/shared/componentsRestyled/coreDesignSystem/PopoverCDS';
import { get } from 'lodash';
import PropTypes from 'prop-types';

import { ConfirmationModalType } from 'constants/confirmationDialog';
import { FeatureFlag } from 'constants/featureFlags';
import {
  FormBuilderMethodType,
  FormBuilderOperationType,
  selectionRequirementOptions,
  SigningSignerType,
  signingTagsOptions,
  SigningTagType,
} from 'constants/formBuilder';
import { defaultPopoverOptions } from 'constants/popover';
import useForm from 'hooks/useForm';
import usePopover from 'hooks/usePopover';
import { reorder } from 'utils/dnd';
import { isPdfFormType as checkIsPdfFormType } from 'utils/forms';

import {
  selectFormApplicationType,
  selectPdfFormHelloSignTree,
  selectRoleBasedSigningSchema,
} from 'store/forms/selectors';
import {
  closeDialogAction,
  closePopupAction,
  getRolesAction,
  openConfirmationDialogAction,
  openDialogAction,
  openPopupAction,
} from 'store/global/actions';
import { selectSecurityGroupOptions } from 'store/global/selectors';

import { formsTestIds } from '../../forms.testIds';
import { dynamicFieldFullFilledStyles } from '../FormEditor.styles';

import ActionButton from './ActionButton';
import SignatureDialogActions from './components/SignatureDialogActions';
import SignatureModeSelector from './components/SignatureModeSelector';
import SignatureOptions from './components/SignatureOptions';
import SignerTypeExtendedConfig from './components/SignerTypeExtendedConfig';
import SigningRuleModal from './components/SigningRuleModal';
import SigningRulesTable from './components/SigningRulesTable';
import {
  deserializeSigningRules,
  executeEditor,
  getAuthorizedSignerModeOption,
  getHelloSignerTypeOptionsByParams,
  getHelloSignTagOptionByParams,
  getSecurityGroupOption,
  getSelectionName,
  isSpecialSignerType,
  normalizeHelloSignTree,
  schema,
} from './Signature.utils';

const Signature = ({
  isNewItem = false,
  editor,
  readOnly,
  dataHellosignTag: hellosignTag = '',
  dataHellosignRequired: required = false,
  dataHellosignSigner = 'signer',
  dataSigningDependencegroupId: dependenceGroupId = '',
  rootDependenceGroupId = '',
  dataSigningDependencegroupName: selectionName = '',
  dataSigningDependencegroupParentId: parentId = '',
  dataSigningId: id = '',
  dataSigningRoleEngineConfig = '',
  dataSigningUsePoaVerbiage = true,
  dataSigningDisplaySignatureLine = null,
  dataSigningAuthorizedSignerMode = '',
  dataSigningAuthorizedSignerUserId = '',
  dataSigningSecurityGroupId = '',
  selectedPage = 1,
  ...props
}) => {
  const pdfHelloSignTree = useSelector(selectPdfFormHelloSignTree(rootDependenceGroupId));
  const roleBasedSigningSchema = useSelector(selectRoleBasedSigningSchema);
  const formApplicationType = useSelector(selectFormApplicationType);
  const securityGroupOptions = useSelector(selectSecurityGroupOptions);

  const ref = useRef();
  const popoverActionRef = useRef();
  const dispatch = useDispatch();
  const { anchorEl, onSetAnchorElement, onClearAnchorElement } = usePopover();
  const getAnchorEl = useCallback(() => ref.current || anchorEl, [anchorEl]);
  const isFFDisabled = !useFeatureToggle(FeatureFlag.HideSignatureLoanOfficerFormFields);

  const isNewExpressionsParsingLogicEnabled = useFeatureToggle(
    FeatureFlag.EnableNewExpressionsParsingLogic,
  );

  const [selectedParentIndex, setSelectedParentIndex] = useState(0);

  const isPdfFormType = checkIsPdfFormType(editor.formType);

  const helloSignTree = useMemo(() => {
    if (!anchorEl) {
      return {};
    }

    return normalizeHelloSignTree({
      isPdfFormType,
      pdfTree: pdfHelloSignTree,
      html: isPdfFormType ? null : editor.getData(),
      id,
      parentId,
    });
  }, [anchorEl, isPdfFormType, pdfHelloSignTree, editor, id, parentId]);

  const signerType = useMemo(
    () =>
      getHelloSignerTypeOptionsByParams({
        formType: editor.formType,
        hellosignTag,
        isFFDisabled,
        applicationType: formApplicationType,
      }).find(({ value }) => value === dataHellosignSigner),
    [editor.formType, hellosignTag, dataHellosignSigner, isFFDisabled, formApplicationType],
  );

  const helloSignTagsFilteredOptions = useMemo(
    () =>
      signingTagsOptions.filter(({ isDisabledWithFF }) =>
        isFFDisabled ? !isDisabledWithFF : true,
      ),
    [isFFDisabled],
  );

  const helloSignTagsMultipleOptions = useMemo(
    () => helloSignTagsFilteredOptions.filter(({ value }) => value !== SigningTagType.DATE),
    [helloSignTagsFilteredOptions],
  );

  const hellosignTagOption = useMemo(
    () =>
      getHelloSignTagOptionByParams({
        formType: editor.formType,
        hellosignTag,
        helloSignTagsOptions: helloSignTagsFilteredOptions,
      }),
    [editor.formType, hellosignTag, helloSignTagsFilteredOptions],
  );

  const initialSigningRules = useMemo(() => {
    if (dataHellosignSigner !== SigningSignerType.ROLE_BASED || !dataSigningRoleEngineConfig) {
      return [];
    }

    return deserializeSigningRules({
      configJson: dataSigningRoleEngineConfig,
      isNewExpressionsParsingLogicEnabled,
    });
  }, [dataHellosignSigner, dataSigningRoleEngineConfig, isNewExpressionsParsingLogicEnabled]);

  const initialForm = useMemo(() => {
    const initialFormValues = {
      hellosignTag: hellosignTagOption,
      required,
      signerType,
      selectionRequirement: selectionRequirementOptions.find(
        ({ value }) => value === helloSignTree.dependenceGroupRequirement,
      ),
      parents: helloSignTree.parents,
      isMultipleOptions: !!dependenceGroupId,
      dependenceGroupId,
      rootDependenceGroupId:
        rootDependenceGroupId || helloSignTree.parents?.[0]?.rootDependenceGroupId,
      signingRules: initialSigningRules,
      usePoaVerbiage: dataSigningUsePoaVerbiage,
      displaySignatureLine: dataSigningDisplaySignatureLine ?? !isPdfFormType,
      authorizedSignerMode: getAuthorizedSignerModeOption(dataSigningAuthorizedSignerMode),
      authorizedSignerUserId: dataSigningAuthorizedSignerUserId || null,
      securityGroup: getSecurityGroupOption(securityGroupOptions, dataSigningSecurityGroupId),
    };

    return initialFormValues;
  }, [
    hellosignTagOption,
    required,
    signerType,
    helloSignTree.parents,
    helloSignTree.dependenceGroupRequirement,
    dependenceGroupId,
    rootDependenceGroupId,
    initialSigningRules,
    dataSigningUsePoaVerbiage,
    dataSigningDisplaySignatureLine,
    isPdfFormType,
    dataSigningAuthorizedSignerMode,
    dataSigningAuthorizedSignerUserId,
    dataSigningSecurityGroupId,
    securityGroupOptions,
  ]);

  const formData = useForm(initialForm);

  const {
    form,
    setSchema,
    handleChange,
    setForm,
    setInForm,
    validationErrors,
    validateForm,
    isFormChanged,
    setInitialFormValues,
    validateField,
  } = formData;

  const formChildren = useMemo(() => {
    if (!helloSignTree) {
      return [];
    }

    const selectedParent = form.parents?.[selectedParentIndex];

    return form.isMultipleOptions ? selectedParent?.children || [] : [];
  }, [helloSignTree, form.parents, form.isMultipleOptions, selectedParentIndex]);

  const isAlwaysRequiredTagType = useMemo(
    () =>
      !!helloSignTagsFilteredOptions.find(option => option.value === form.hellosignTag?.value)
        ?.required,
    [form.hellosignTag, helloSignTagsFilteredOptions],
  );

  const isRoleBasedSigner = useMemo(
    () => form.signerType?.value === SigningSignerType.ROLE_BASED,
    [form.signerType?.value],
  );

  const isSpecialSigner = isRoleBasedSigner || isSpecialSignerType(form.signerType);

  const isAllBorrowersSignersNotApplicableToHelloSignTag = useMemo(
    () =>
      !isPdfFormType &&
      isFormChanged &&
      form.hellosignTag?.value !== SigningTagType.SIGNATURE &&
      form.signerType?.value === SigningSignerType.ALL_BORROWERS,
    [isFormChanged, form.hellosignTag?.value, form.signerType?.value, isPdfFormType],
  );

  const isRoleBasedSignerNotApplicableToHelloSignTag = useMemo(
    () =>
      !isPdfFormType &&
      isFormChanged &&
      form.hellosignTag?.value !== SigningTagType.SIGNATURE &&
      isSpecialSigner,
    [isFormChanged, form.hellosignTag?.value, isSpecialSigner, isPdfFormType],
  );

  const previewInputPlaceholder = useMemo(() => {
    return `${hellosignTagOption?.name}${
      signerType?.name ? ` (${signerType?.name})${selectionName ? ` [${selectionName}]` : ''}` : ''
    }`;
  }, [hellosignTagOption?.name, signerType?.name, selectionName]);

  const helloSignSignerTypeOptionsByParams = useMemo(
    () =>
      getHelloSignerTypeOptionsByParams({
        formType: editor.formType,
        hellosignTag: form.hellosignTag?.value,
        isFFDisabled,
        applicationType: formApplicationType,
      }),
    [editor.formType, form.hellosignTag?.value, isFFDisabled, formApplicationType],
  );

  useEffect(() => {
    const action = anchorEl ? openPopupAction : closePopupAction;
    dispatch(action());

    if (anchorEl && !securityGroupOptions.length) {
      dispatch(getRolesAction());
    }
  }, [anchorEl, securityGroupOptions.length, dispatch]);

  useEffect(() => {
    setForm(initialForm);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [setForm, anchorEl, securityGroupOptions.length]);

  useEffect(() => {
    setSchema(schema);
  }, [setSchema]);

  useEffect(() => {
    if (!isNewItem) {
      return;
    }

    onSetAnchorElement(null, ref);
  }, [isNewItem, onSetAnchorElement]);

  useEffect(() => {
    if (!isAlwaysRequiredTagType) {
      return;
    }

    setInForm('required', true, false);
  }, [isAlwaysRequiredTagType, setInForm]);

  useEffect(() => {
    if (!isSpecialSigner || !form.isMultipleOptions) {
      return;
    }

    setInForm('isMultipleOptions', false, false);
  }, [isSpecialSigner, form.isMultipleOptions, setInForm]);

  useEffect(() => {
    if (
      !isAllBorrowersSignersNotApplicableToHelloSignTag &&
      !isRoleBasedSignerNotApplicableToHelloSignTag
    ) {
      return;
    }

    setInForm('signerType', null, false);
  }, [
    isAllBorrowersSignersNotApplicableToHelloSignTag,
    isRoleBasedSignerNotApplicableToHelloSignTag,
    setInForm,
  ]);

  const handleClose = useCallback(
    e => {
      const onAccept = () => {
        if (isNewItem) {
          editor.commands.get(FormBuilderOperationType.UNDO).execute(props);
        }

        setForm(initialForm);
        onClearAnchorElement();

        editor.editing?.view.focus();
      };

      const params = {
        type: ConfirmationModalType.WARNING,
        message: 'Confirm cancel',
        description: 'Are you sure you want to cancel these changes?',
        onAccept,
      };

      if (e && e.type !== 'submit' && isFormChanged) {
        dispatch(openConfirmationDialogAction(params));

        return;
      }

      onAccept();
    },
    [dispatch, editor, isNewItem, initialForm, setForm, isFormChanged, props, onClearAnchorElement],
  );

  const onInputItemClick = useCallback(
    e => {
      if (readOnly) {
        return;
      }

      onSetAnchorElement({ ...e });
    },
    [readOnly, onSetAnchorElement],
  );

  const onSingleOptionDelete = useCallback(() => {
    const params = {
      type: ConfirmationModalType.WARNING,
      message: 'Confirm deleting',
      description: 'Are you sure you want to delete this item?',
      onAccept: () => {
        editor.execute(FormBuilderMethodType.DELETE_REACT_COMPONENT, props);
        handleClose();
      },
    };

    dispatch(openConfirmationDialogAction(params));
  }, [dispatch, editor, handleClose, props]);

  const onSubmit = useCallback(
    async e => {
      e.preventDefault();

      if (!(await validateForm())) {
        return;
      }

      executeEditor({
        editor,
        form,
        props,
        isEdit: !!hellosignTag,
        isNewItem,
        isPdfFormType,
        selectedPage,
        dependenceGroupId,
      });

      editor.editing?.view.focus();

      onClearAnchorElement();

      setInitialFormValues(form);
    },
    [
      validateForm,
      editor,
      form,
      props,
      hellosignTag,
      isNewItem,
      isPdfFormType,
      selectedPage,
      dependenceGroupId,
      onClearAnchorElement,
      setInitialFormValues,
    ],
  );

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const onAutocompleteChange = useCallback(
    onAutocompleteChangeInForm({ setInForm, validateField }),
    [setInForm, validateField],
  );

  const onParentClick = useCallback(
    e => {
      const { index } = e.currentTarget.dataset;
      setSelectedParentIndex(Number(index));
    },
    [setSelectedParentIndex],
  );

  const handleCloseDialog = useCallback(() => {
    dispatch(closeDialogAction());
  }, [dispatch]);

  const handleSaveNewRule = useCallback(
    rule => {
      const updatedRules = [...(form.signingRules || []), rule];
      dispatch(closeDialogAction());
      requestAnimationFrame(() => {
        setInForm('signingRules', updatedRules);
        requestAnimationFrame(() => popoverActionRef.current?.updatePosition());
      });
    },
    [form.signingRules, setInForm, dispatch],
  );

  const onAddSigningRule = useCallback(() => {
    dispatch(
      openDialogAction({
        header: 'Add Signing Rule',
        size: 'fit-content',
        body: () => (
          <SigningRuleModal
            schemaParams={roleBasedSigningSchema}
            onCancel={handleCloseDialog}
            onSave={handleSaveNewRule}
          />
        ),
      }),
    );
  }, [dispatch, roleBasedSigningSchema, handleCloseDialog, handleSaveNewRule]);

  const onEditSigningRule = useCallback(
    index => {
      const ruleToEdit = form.signingRules[index];

      const handleSaveEditedRule = updatedRule => {
        const updatedRules = form.signingRules.map((r, i) => (i === index ? updatedRule : r));
        dispatch(closeDialogAction());
        requestAnimationFrame(() => {
          setInForm('signingRules', updatedRules);
          requestAnimationFrame(() => popoverActionRef.current?.updatePosition());
        });
      };

      dispatch(
        openDialogAction({
          header: 'Edit Signing Rule',
          size: 'fit-content',
          body: () => (
            <SigningRuleModal
              rule={ruleToEdit}
              schemaParams={roleBasedSigningSchema}
              onCancel={handleCloseDialog}
              onSave={handleSaveEditedRule}
            />
          ),
        }),
      );
    },
    [dispatch, roleBasedSigningSchema, form.signingRules, setInForm, handleCloseDialog],
  );

  const onDeleteSigningRule = useCallback(
    index => {
      const params = {
        type: ConfirmationModalType.WARNING,
        message: 'Confirm deleting',
        description: 'Are you sure you want to delete this signing rule?',
        onAccept: () => {
          const updatedRules = form.signingRules.filter((_, i) => i !== index);
          setInForm('signingRules', updatedRules);
        },
      };

      dispatch(openConfirmationDialogAction(params));
    },
    [dispatch, form.signingRules, setInForm],
  );

  const onSelectSingleMode = useCallback(() => {
    setInForm('isMultipleOptions', false);
  }, [setInForm]);

  const onSelectMultiOptionMode = useCallback(() => {
    setInForm('isMultipleOptions', true);
  }, [setInForm]);

  const onReorderSigningRules = useCallback(
    ({ source, destination }) => {
      if (!destination || source.index === destination.index) {
        return;
      }

      const reorderedRules = reorder({
        list: form.signingRules,
        startIndex: source.index,
        endIndex: destination.index,
      });

      setInForm('signingRules', reorderedRules);
    },
    [form.signingRules, setInForm],
  );

  return (
    <span>
      <Box
        component="span"
        ref={ref}
        sx={dynamicFieldFullFilledStyles({ isPdfFormType })}
        title={hellosignTag ? previewInputPlaceholder : ''}
        onClick={onInputItemClick}
      >
        {!!hellosignTag && previewInputPlaceholder}
      </Box>

      {!!anchorEl && (
        <PopoverCDS
          disableEnforceFocus
          action={popoverActionRef}
          anchorEl={getAnchorEl}
          anchorOrigin={defaultPopoverOptions.anchorOrigin}
          open={!!anchorEl}
          title="Sign Tag"
          transformOrigin={defaultPopoverOptions.transformOrigin}
          onClose={handleClose}
        >
          <form noValidate className="flex flex-col gap-24 min-w-[358px]" onSubmit={onSubmit}>
            <div className="flex flex-col gap-16">
              {!isSpecialSigner && (
                <SignatureModeSelector
                  isMultipleOptions={!!form.isMultipleOptions}
                  onSelectMultiOptionMode={onSelectMultiOptionMode}
                  onSelectSingleMode={onSelectSingleMode}
                />
              )}

              {!isSpecialSigner && !form.isMultipleOptions && (
                <div className="flex flex-col gap-8">
                  <Typography color="text.secondary" variant="bold12">
                    Field
                  </Typography>
                  <RoundedBox
                    className="flex flex-col gap-12 !h-fit min-w-[358px]"
                    menu={
                      <ActionButton isSingleOption onSingleOptionDelete={onSingleOptionDelete} />
                    }
                  >
                    <AutocompleteCDS
                      autoHighlight
                      required
                      dataTestId={formsTestIds.SIGNATURE_TYPE_SELECT}
                      error={!!validationErrors.hellosignTag}
                      helperText={validationErrors.hellosignTag}
                      label="Type"
                      name="hellosignTag"
                      options={helloSignTagsFilteredOptions}
                      value={form.hellosignTag}
                      onChange={onAutocompleteChange}
                    />
                    <FormGeneratorSwitchCDS
                      disabled={isAlwaysRequiredTagType}
                      error={!!validationErrors.required}
                      helperText={validationErrors.required}
                      label="Required"
                      name="required"
                      value={form.required || isAlwaysRequiredTagType}
                      onChange={handleChange}
                    />
                  </RoundedBox>
                </div>
              )}

              <div className="flex flex-col gap-8">
                <Typography color="text.secondary" variant="bold12">
                  Signer
                </Typography>
                <AutocompleteCDS
                  autoHighlight
                  required
                  className="min-w-[358px]"
                  dataTestId={formsTestIds.SIGNATURE_SIGNER_SELECT}
                  error={!!validationErrors.signerType}
                  helperText={validationErrors.signerType}
                  label="Signer"
                  name="signerType"
                  options={helloSignSignerTypeOptionsByParams}
                  value={form.signerType}
                  onChange={onAutocompleteChange}
                />

                {isRoleBasedSigner && (
                  <RoundedBox className="flex flex-col gap-12 min-w-[358px]">
                    <SigningRulesTable
                      rules={form.signingRules}
                      onAdd={onAddSigningRule}
                      onDelete={onDeleteSigningRule}
                      onDragEnd={onReorderSigningRules}
                      onEdit={onEditSigningRule}
                    />
                    {!!validationErrors.signingRules && (
                      <span className="text-red-500 text-12">{validationErrors.signingRules}</span>
                    )}
                  </RoundedBox>
                )}

                <SignerTypeExtendedConfig
                  form={form}
                  securityGroupOptions={securityGroupOptions}
                  validationErrors={validationErrors}
                  onAutocompleteChange={onAutocompleteChange}
                />
              </div>

              {((!form.isMultipleOptions &&
                form.hellosignTag?.value === SigningTagType.SIGNATURE) ||
                isSpecialSigner) && (
                <SignatureOptions
                  displaySignatureLine={!!form.displaySignatureLine}
                  usePoaVerbiage={!!form.usePoaVerbiage}
                  onChange={handleChange}
                />
              )}

              {!isSpecialSigner && form.isMultipleOptions && (
                <div className="flex flex-col gap-8">
                  <Typography color="text.secondary" variant="bold12">
                    Multi-option tree
                  </Typography>
                  <RoundedBox className="flex flex-col gap-12">
                    <AutocompleteCDS
                      autoHighlight
                      required
                      className="max-w-[358px]"
                      dataTestId={formsTestIds.SIGNATURE_SELECTION_REQUIREMENT_SELECT}
                      error={!!validationErrors.selectionRequirement}
                      helperText={validationErrors.selectionRequirement}
                      label="Selection requirement"
                      name="selectionRequirement"
                      options={selectionRequirementOptions}
                      value={form.selectionRequirement}
                      onChange={onAutocompleteChange}
                    />
                    <div className="flex gap-12">
                      <div className="flex flex-col gap-12 min-w-[358px]">
                        {!!form.parents?.length &&
                          form.parents.map(({ id, rootDependenceGroupId }, index) => {
                            const hellosignTagPathName = `parents[${index}].hellosignTag`;
                            const hellosignTagValue = get(form, hellosignTagPathName);
                            const error = get(validationErrors, hellosignTagPathName);
                            const disabled = selectedParentIndex !== index;
                            const childDependenceGroupId = get(
                              form,
                              `parents[${index}].children[0].dependenceGroupId`,
                            );

                            return (
                              <RoundedBox
                                className="cursor-pointer !h-fit"
                                data-index={index}
                                disabled={disabled}
                                key={index}
                                menu={
                                  <ActionButton
                                    {...formData}
                                    isParent
                                    dependenceGroupId={childDependenceGroupId}
                                    parentId={id}
                                    parentIndex={index}
                                    rootDependenceGroupId={rootDependenceGroupId}
                                    setSelectedParentIndex={setSelectedParentIndex}
                                  />
                                }
                                onClick={onParentClick}
                              >
                                <AutocompleteCDS
                                  autoHighlight
                                  required
                                  dataTestId={formsTestIds.getSignatureParentTagSelectTestId(index)}
                                  disabled={disabled}
                                  error={!!error}
                                  helperText={error}
                                  label={getSelectionName(index)}
                                  name={hellosignTagPathName}
                                  options={helloSignTagsMultipleOptions}
                                  value={hellosignTagValue}
                                  onChange={onAutocompleteChange}
                                />
                              </RoundedBox>
                            );
                          })}
                      </div>
                      <div className="flex flex-col gap-12 ">
                        {!!formChildren?.length &&
                          formChildren.map(
                            ({ dependenceGroupParentId, dependenceGroupId }, index) => {
                              const pathName = `parents[${selectedParentIndex}].children[${index}]`;
                              const hellosignTagPathName = `${pathName}.hellosignTag`;
                              const requiredPathName = `${pathName}.required`;

                              const hellosignTagValue = get(form, hellosignTagPathName);
                              const helloSignTagError = get(validationErrors, hellosignTagPathName);
                              const requiredValue = get(form, requiredPathName);
                              const requiredError = get(validationErrors, requiredPathName);
                              const rootDependenceGroupId = get(
                                form,
                                `parents[0].rootDependenceGroupId`,
                              );

                              return (
                                <RoundedBox
                                  className="flex flex-col gap-12 !h-fit min-w-[358px]"
                                  key={index}
                                  menu={
                                    <ActionButton
                                      {...formData}
                                      childIndex={index}
                                      dependenceGroupId={dependenceGroupId}
                                      parentId={dependenceGroupParentId}
                                      parentIndex={selectedParentIndex}
                                      rootDependenceGroupId={rootDependenceGroupId}
                                    />
                                  }
                                >
                                  <AutocompleteCDS
                                    autoHighlight
                                    required
                                    dataTestId={formsTestIds.getSignatureChildTagSelectTestId(
                                      index,
                                    )}
                                    error={!!helloSignTagError}
                                    helperText={helloSignTagError}
                                    label={getSelectionName(selectedParentIndex, index)}
                                    name={hellosignTagPathName}
                                    options={helloSignTagsMultipleOptions}
                                    value={hellosignTagValue}
                                    onChange={onAutocompleteChange}
                                  />
                                  <FormGeneratorSwitchCDS
                                    checked={requiredValue || false}
                                    data-testid={requiredPathName}
                                    error={!!requiredError}
                                    helperText={requiredError}
                                    id={requiredPathName}
                                    label="Required"
                                    name={requiredPathName}
                                    onChange={handleChange}
                                  />
                                </RoundedBox>
                              );
                            },
                          )}
                      </div>
                    </div>
                  </RoundedBox>
                </div>
              )}
            </div>

            <SignatureDialogActions isSaveDisabled={!isFormChanged} onCancel={handleClose} />
          </form>
        </PopoverCDS>
      )}
    </span>
  );
};

Signature.propTypes = {
  editor: PropTypes.object.isRequired,
  readOnly: PropTypes.bool.isRequired,
  dataHellosignTag: PropTypes.string,
  isNewItem: PropTypes.bool,
  dataHellosignRequired: PropTypes.bool,
  dataHellosignSigner: PropTypes.string,
  dataSigningDependencegroupId: PropTypes.string,
  selectedPage: PropTypes.number,
  dataSigningDependencegroupName: PropTypes.string,
  dataSigningId: PropTypes.string,
  dataSigningDependencegroupParentId: PropTypes.string,
  rootDependenceGroupId: PropTypes.string,
  dataSigningRoleEngineConfig: PropTypes.string,
  dataSigningUsePoaVerbiage: PropTypes.bool,
  dataSigningDisplaySignatureLine: PropTypes.bool,
  dataSigningAuthorizedSignerMode: PropTypes.string,
  dataSigningAuthorizedSignerUserId: PropTypes.string,
  dataSigningSecurityGroupId: PropTypes.string,
};

export default memo(Signature);
